import type { CSSProperties } from "react";

type RGB = [number, number, number];

function luminance(rgb: RGB): number {
  const channels = rgb.map((channel) => {
    const value = channel / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
}

function mix(rgb: RGB, target: number, amount: number): RGB {
  return rgb.map((channel) =>
    Math.round(channel * (1 - amount) + target * amount),
  ) as RGB;
}

function hex(rgb: RGB): string {
  return `#${rgb.map((value) => value.toString(16).padStart(2, "0")).join("")}`;
}

function foreground(rgb: RGB): string {
  return luminance(rgb) > 0.179 ? "#000000" : "#ffffff";
}

// Used only by the vehicles and real_estate storefronts and their footer.
export function dedicatedStoreTheme(
  primaryColor: string,
  fallbackColor: string,
): CSSProperties {
  const primary = /^#[0-9a-f]{6}$/i.test(primaryColor)
    ? primaryColor
    : fallbackColor;
  const rgb = [1, 3, 5].map((offset) =>
    parseInt(primary.slice(offset, offset + 2), 16),
  ) as RGB;
  const hover = mix(rgb, 0, 0.15);
  const soft = mix(rgb, 255, 0.94);
  const muted = mix(rgb, 255, 0.65);
  let text = rgb;
  // Muted is the darkest secondary surface; this also covers soft and white.
  while ((luminance(muted) + 0.05) / (luminance(text) + 0.05) < 4.5) {
    text = mix(text, 0, 0.15);
  }
  return {
    "--store-primary": primary,
    "--store-on-primary": foreground(rgb),
    "--store-primary-hover": hex(hover),
    "--store-on-primary-hover": foreground(hover),
    "--store-primary-soft": hex(soft),
    "--store-primary-muted": hex(muted),
    "--store-primary-text": hex(text),
    "--store-primary-overlay": hex(mix(rgb, 0, 0.55)),
    "--footer-background": primary,
    "--footer-foreground": foreground(rgb),
  } as CSSProperties;
}
